// --------------------------------------------------------------
// Created On: 2021-10-05
// Author: Zachary Thomas
//
// Last Modified: 2026-01-05
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import NavigationDrawerCollection from "./NavigationDrawerCollection/NavigationDrawerCollection";
import NavigationDrawerLink from "./NavigationDrawerLink/NavigationDrawerLink";
import SearchBar from "../SearchBar/SearchBar";
import Spinner from "../Spinner/Spinner";
import userHasPermission from "../../utilities/userHasPermission";
import { PUBLIC_PAGES, RPM_COMPANY, ENABLE_END_USER_COMPANY_CREATION } from "../../constants/miscellaneous";
import useReduxSelector from "src/hooks/useReduxSelector";
import {
  CREATE_ROLES_PERMISSION,
  CREATE_USER_GROUPS_PERMISSION,
  DELETE_ROLES_PERMISSION,
  DELETE_USERS_PERMISSION,
  DELETE_USER_GROUPS_PERMISSION,
  INVITE_USERS_PERMISSION,
  UPDATE_ROLES_PERMISSION,
  UPDATE_USERS_PERMISSION,
  UPDATE_USER_GROUPS_PERMISSION,
  CREATE_COMPANY_PERMISSION,
  UPDATE_COMPANY_PERMISSION,
  DELETE_COMPANY_PERMISSION,
  PREPARE_QUOTES_PERMISSION,
} from "../../constants/permissions";
import userHasRolePermissions from "../../utilities/userHasRolePermissions";
import userHasUserPermissions from "../../utilities/userHasUserPermissions";
import styles from "./NavigationDrawer.module.scss";

// Navigation drawer for navigating pages on a mobile device.
export default function NavigationDrawer(props: Props): Component {
  const currentPageIsPublic = PUBLIC_PAGES.includes(props.currentPage);
  const [loading, setLoading] = useState<boolean>(false);
  const [inCompany, setInCompany] = useState<boolean>(true);
  const [startSearch, setStartSearch] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Keep track of the status of the current user for routing and rendering purposes.
  useEffect(() => {
    setInCompany(currentUser.companyName !== RPM_COMPANY);
  }, [JSON.stringify(currentUser)]);

  return (
    <div>
      <Spinner loading={loading} />

      <div
        className={`${styles.overlay} ${props.showNavigationDrawer ? styles.visible : ""}`}
        onClick={() => props.onCloseNavigationDrawer()}
      />

      <nav className={`${styles.drawer} ${props.showNavigationDrawer ? styles.visible : ""}`} data-test="nav-drawer">
        <div>
          {props.currentPage === "/account" && (
            <NavigationDrawerCollection
              name="Account Settings"
              icon="user"
              targetPage={["/account/user-info", "/account/password"]}
              currentPage={props.currentPage}
            >
              <Fragment>
                <NavigationDrawerLink
                  name="Account Info"
                  targetPage="/account/user-info"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Change Password"
                  targetPage="/account/password"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              </Fragment>
            </NavigationDrawerCollection>
          )}

          {inCompany ? (
            <Fragment>
              {!currentPageIsPublic && (
                <Fragment>
                  <NavigationDrawerLink
                    name="Dashboard"
                    targetPage="/dashboard/general"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />

                  <NavigationDrawerLink
                    name="Quick Order"
                    targetPage="/quick-order"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />

                  {userHasPermission([
                    [INVITE_USERS_PERMISSION],
                    [UPDATE_USERS_PERMISSION],
                    [DELETE_USERS_PERMISSION],
                    [CREATE_USER_GROUPS_PERMISSION],
                    [UPDATE_USER_GROUPS_PERMISSION],
                    [DELETE_USER_GROUPS_PERMISSION],
                  ]) && (
                    <NavigationDrawerLink
                      name="Manage Users"
                      targetPage="/manage/users"
                      onClick={() => props.onCloseNavigationDrawer()}
                    />
                  )}

                  {userHasPermission([
                    [CREATE_ROLES_PERMISSION],
                    [UPDATE_ROLES_PERMISSION],
                    [DELETE_ROLES_PERMISSION],
                  ]) && (
                    <NavigationDrawerLink
                      name="Manage Roles"
                      targetPage="/manage/roles"
                      onClick={() => props.onCloseNavigationDrawer()}
                    />
                  )}

                  {userHasPermission([[PREPARE_QUOTES_PERMISSION]]) && (
                    <NavigationDrawerLink
                      name="Manage Quotes"
                      targetPage="/sales-quotes"
                      onClick={() => props.onCloseNavigationDrawer()}
                    />
                  )}

                  <NavigationDrawerLink
                    name="Order History"
                    targetPage="/order-history"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                </Fragment>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <NavigationDrawerLink
                name="Companies Dashboard"
                targetPage="/dashboard/companies"
                onClick={() => props.onCloseNavigationDrawer()}
              />

              {userHasPermission([
                [CREATE_COMPANY_PERMISSION],
                [UPDATE_COMPANY_PERMISSION],
                [DELETE_COMPANY_PERMISSION],
              ]) && (
                <NavigationDrawerLink
                  name="Manage Companies"
                  targetPage="/manage/companies"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              )}
              {userHasPermission([[UPDATE_COMPANY_PERMISSION]]) && (
                <NavigationDrawerLink
                  name="Distributors"
                  targetPage="/manage-company-distributors"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              )}
              {userHasUserPermissions() && (
                <NavigationDrawerLink
                  name="Manage Users"
                  targetPage="/manage/users"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              )}
              {userHasRolePermissions() && (
                <NavigationDrawerLink
                  name="Manage Roles"
                  targetPage="/manage/roles"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              )}
            </Fragment>
          )}

          {currentPageIsPublic && (
            <Fragment>
              <NavigationDrawerCollection
                name="Account"
                icon="sign-in"
                targetPage={["/login", "/register", "/forgot-password"]}
                currentPage={props.currentPage}
              >
                <NavigationDrawerLink
                  name="Login"
                  targetPage="/login"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                {ENABLE_END_USER_COMPANY_CREATION ? (
                  <NavigationDrawerLink
                    name="Create Account"
                    targetPage="/register"
                    onClick={() => props.onCloseNavigationDrawer()}
                  />
                ) : (
                  <Fragment>
                    {/* Only writing this as a ternary due to a child not being allowed to be a boolean. */}
                  </Fragment>
                )}
                <NavigationDrawerLink
                  name="Reset Password"
                  targetPage="/forgot-password"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              </NavigationDrawerCollection>
              <NavigationDrawerCollection
                name="Legal"
                icon="gavel"
                targetPage={["/privacy-policy-text", "/terms-and-conditions-of-sale-text"]}
                currentPage={props.currentPage}
              >
                <NavigationDrawerLink
                  name="Privacy Policy Text"
                  targetPage="/privacy-policy-text"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
                <NavigationDrawerLink
                  name="Terms and Conditions of Sale Text"
                  targetPage="/terms-and-conditions-of-sale-text"
                  onClick={() => props.onCloseNavigationDrawer()}
                />
              </NavigationDrawerCollection>
            </Fragment>
          )}
        </div>

        {!currentPageIsPublic && inCompany && (
          <div className={styles.search}>
            <div className="m-3">
              <SearchBar
                isInNavbar={false}
                isInNavDrawer={true}
                isInDashboard={false}
                startSearch={startSearch}
                errorMessage=""
                onSearchComplete={() => setStartSearch(false)}
                onErrorMessageChange={(errorMessage) => props.onErrorMessageChange(errorMessage)}
                onLoadingChange={(loading) => setLoading(loading)}
                onClose={() => props.onCloseNavigationDrawer()}
              />
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}

interface Props {
  currentPage: string;
  showNavigationDrawer: boolean;
  onCloseNavigationDrawer: () => void;
  onErrorMessageChange: (errorMessage: string) => void;
}
